/* ====== Global helper functions ====== */

// Function to find a specific node of a data tree using its key
export const findNodeByKey = (key, nodes = []) => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(key, node.children);
      if (found) return found;
    }
  }
  return null;
};

/**
 * naturalCompare(a, b)
 *
 * Splits each input string into numeric and non-numeric segments,
 * then compares segment-by-segment:
 *   - If both segments are purely digits, compare them as numbers.
 *   - Otherwise, compare them as case-insensitive strings.
 * Falls back to segment count if all segments match.
 *
 * Handles:
 *   • Numeric-only tags:      "2"  < "10"
 *   • Decimal-style tags:     "3"  < "3.1"  < "3.10"
 *   • Alphanumeric tags:      "A.1" < "A.2" < "B-1" < "B-10"
 *   • Mixed dash/dot + letters
 *
 * @param {string} a – first tag to compare
 * @param {string} b – second tag to compare
 * @returns {number} negative if a < b, positive if a > b, zero if equal
 */
export const naturalCompare = (a, b) => {
  // RegExp to capture runs of digits (\d+) or runs of non-digits (\D+)
  const segmentRE = /(\d+)|(\D+)/g;

  // Split each string into an array of segments
  const segA = a.match(segmentRE) || [];
  const segB = b.match(segmentRE) || [];

  // Compare corresponding segments
  const len = Math.min(segA.length, segB.length);
  for (let i = 0; i < len; i++) {
    const sA = segA[i];
    const sB = segB[i];

    // If identical text, move on to next segment
    if (sA === sB) continue;

    // Try parsing both as integers
    const nA = parseInt(sA, 10);
    const nB = parseInt(sB, 10);
    const isNumA = !isNaN(nA) && String(nA) === sA;
    const isNumB = !isNaN(nB) && String(nB) === sB;

    // If both segments are valid whole numbers, compare numerically
    if (isNumA && isNumB) {
      return nA - nB;
    }

    // Otherwise compare as case-insensitive strings (ignoring locale nuances)
    return sA.localeCompare(sB, undefined, { sensitivity: "base" });
  }

  // All shared segments matched; shorter array wins (e.g. "3" < "3.1")
  return segA.length - segB.length;
};

// Resolves the first non-null field from an object based on a list of field names
export function resolveId(obj, ...fields) {
  for (const f of fields) {
    if (obj?.[f] != null) return String(obj[f]);
  }
  return null;
};

/**
 * Loose equality check: strict equality first, then string-coerced equality.
 * Handles number/string ID mismatches (e.g. 42 === "42") without the pitfalls
 * of == (no type coercion beyond String()).
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function isSameValue(a, b) {
  return a === b || String(a) === String(b);
}

/**
 * Normalizes a route/ID parameter from URL params, row IDs, or API responses.
 * Rejects null/undefined, empty strings, and literal "undefined"/"null" strings
 * that result from bad navigations or missing data.
 *
 * @param {unknown} raw
 * @returns {string|null} trimmed string, or null when invalid
 */
export function normalizeRouteIdParam(raw) {
  if (raw == null) return null;
  const s = typeof raw === "string" ? raw.trim() : (typeof raw === "number" && Number.isFinite(raw) ? String(raw) : null);
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === "undefined" || lower === "null") return null;
  return s;
}